import axios from "axios";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    // Always initialize from localStorage
    const [token, setToken_] = useState(() => localStorage.getItem("token"));

    // Always sync state and localStorage
    const setToken = (newToken) => {
        console.log('🟠 Setting token:', newToken ? 'token present' : 'no token');
        setToken_(newToken);
        if (newToken) {
            localStorage.setItem('token', newToken);
        } else {
            localStorage.removeItem('token');
        }
    };

    const clearToken = () => {
        console.log('🟠 Clearing token...');

        // Clear token and local storage
        setToken(null);
        localStorage.removeItem('token');
    };

    // Set up axios headers when token changes
    useEffect(() => {
        if (token) {
            console.log('🟠 Setting axios authorization header');
            axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        } else {
            console.log('🟠 Removing axios authorization header');
            delete axios.defaults.headers.common["Authorization"];
        }
    }, [token]);

    /**
     * Get current user from localStorage or state
     * @returns {object|null}
     */
    const getCurrentUser = () => {
        // First try to get user data from localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const userData = JSON.parse(userStr);
                return userData;
            } catch (error) {
            }
        }

        // If no user data in localStorage, try to decode JWT token
        if (token) {
            try {
                // Check if token is a JWT (has 3 parts separated by dots)
                if (token.includes('.') && token.split('.').length === 3) {
                    // Decode JWT token (without verification for client-side use)
                    const payload = JSON.parse(atob(token.split('.')[1]));

                    return {
                        id: payload.userId || payload.id,
                        email: payload.email,
                        firstName: payload.firstName,
                        lastName: payload.lastName,
                        role: payload.role, // This will be "Trader" or "Admin"
                        permissions: payload.permission || payload.permissions || [], // Array of permissions
                        permissionCount: payload.permissionCount
                    };
                } else {
                    // If token is not a JWT, return minimal user object
                    return {
                        id: token,
                        email: 'unknown@example.com',
                        firstName: 'User',
                        lastName: 'Unknown',
                        role: 'Trader', // Default to Trader if no role info
                        permissions: [],
                        permissionCount: 0
                    };
                }
            } catch (error) {
                // Error decoding token, continue to fallback
            }
        }

        // Fallback: return null if no token or decoding fails
        return null;
    };

    /**
     * Check if current user is Admin or MasterAdmin
     * @returns {boolean}
     */
    const isAdmin = () => {
        const user = getCurrentUser();
        if (!user) {
            return false;
        }

        // UPDATED: Support for independent DataFeed.Server roles
        // Roles: "MasterAdmin" (full control) or "Admin" (operations only)
        const isAdminUser =
            user.role === 'Admin' ||
            user.role === 'MasterAdmin' ||  // NEW: Independent auth MasterAdmin
            user.role === 1 ||
            user.role === '1' ||  // String "1"
            user.role === 'admin' ||
            user.role === 'ADMIN' ||
            (typeof user.role === 'number' && user.role === 1) ||
            (typeof user.role === 'string' && user.role.toLowerCase() === 'admin') ||
            (typeof user.role === 'string' && user.role.toLowerCase() === 'masteradmin') ||  // NEW
            (typeof user.role === 'string' && user.role === '1');

        return isAdminUser;
    };

    /**
     * Check if current user is MasterAdmin (for user management pages)
     * @returns {boolean}
     */
    const isMasterAdmin = () => {
        const user = getCurrentUser();
        if (!user) {
            return false;
        }

        return user.role === 'MasterAdmin' ||
               (typeof user.role === 'string' && user.role.toLowerCase() === 'masteradmin');
    };

    /**
     * Check if current user is Trader
     * @returns {boolean}
     */
    const isTrader = () => {
        const user = getCurrentUser();
        if (!user) return false;
        return user.role === 'Trader' || user.role === 0 || user.role === 'trader';
    };

    /**
     * Check if user has specific permission (client-side check)
     * Note: This is for UI only. Backend still validates on each request.
     * @param {string} permissionKey - e.g., "Settings.LPManagement"
     * @returns {boolean}
     */
    const hasPermission = (permissionKey) => {
        const user = getCurrentUser();
        if (!user) {
            return false;
        }

        // Admins have all permissions
        if (isAdmin()) {
            return true;
        }

        // Check if trader has the permission
        if (isTrader() && user.permissions) {
            // Handle both array of strings and array of objects
            if (Array.isArray(user.permissions)) {
                const hasPerm = user.permissions.some(permission => {
                    // If permission is a string, check directly
                    if (typeof permission === 'string') {
                        return permission === permissionKey;
                    }
                    // If permission is an object, check permissionKey property
                    if (typeof permission === 'object' && permission.permissionKey) {
                        return permission.permissionKey === permissionKey;
                    }
                    return false;
                });
                return hasPerm;
            }
        }

        return false;
    };

    const contextValue = useMemo(() => ({
        clearToken,
        token,
        setToken,
        isAdmin,
        isMasterAdmin,  // NEW: Added for user management access control
        isTrader,
        hasPermission,
        getCurrentUser
    }), [token]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
